'use es6';
'use es6';

import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import partial from 'transmute/partial';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UILink from 'UIComponents/link/UILink';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import BizObjectivesMap from 'self-service-api/constants/BizObjectivesMap';
import { STREAMLINE_B2B_BUYING, GROW_YOUR_REVENUE } from 'self-service-api/constants/BizObjectives';
import { PAYMENTS } from 'self-service-api/constants/Features';
import { getLangEligibleFeatureIds } from 'self-service-api/api/getLangEligibleFeatureIds';
import { getProductFeatureDescriptionKey } from 'self-service-api/core/utilities/getFeatureDescriptionKey';
import HighlightedProductsBizObjectives from 'self-service-api/constants/HighlightedProductsBizObjectives';
import { FREE, STARTER } from 'self-service-api/constants/ProductLevels';
import { CMS } from 'self-service-api/constants/ProductLines';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { isExperimentTreatmentRequestLoadingSelector, isMon569VariantSelector, mon607VariantSelector } from '../../../selectors/ExperimentTreatmentSelectors';
import ChecklistItem from 'pricing-pages-unified-ui/components/common/ChecklistItem';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIBadge from 'UIComponents/badge/UIBadge';
import { HighlightedProductsHeaderConfig } from '../../../constants/HighlightedProductsHeaderConfig';
import { Section } from '../../common/MerchCardSharedComponents';
import FeatureModalSplitter from '../features/FeatureModalSplitter';
import { MON607_BIZ_OBJECTIVE_CATEGORIES } from '../../../constants/Mon607BizObjectiveCategories';
import { MON607_PARAMETER_OPTIONS } from '../../../constants/ExperimentConstants';
import { BizLowerTierContainer, BizObjHeaderLink, BizObjHeaderNoLink } from './BusinessObjectivesStyledComponents';

const BizObjectives = ({
  productLine,
  productLevel,
  commonStyles
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isExperimentTreatmentRequestLoading = useSelector(isExperimentTreatmentRequestLoadingSelector);
  const mon607Variant = useSelector(mon607VariantSelector);

  const onBizObjHeaderClick = bizObjKey => getUsageTracker().track('bizObjectiveClick', {
    bizObjectiveKey: bizObjKey,
    isLink: Boolean(BizObjectivesMap[bizObjKey].breakoutData)
  });

  const toggleViewAllFeaturesModal = () => {
    setIsModalOpen(!isModalOpen);

    if (!isModalOpen) {
      getUsageTracker().track('clickToViewModal', {
        name: 'compare-feature-modal',
        productLine,
        productLevel,
        location: 'merch-card-description'
      });
    }
  };

  const renderBizObjectiveHeader = bizObjKey => {
    const bizObj = BizObjectivesMap[bizObjKey];

    const textNode = /*#__PURE__*/_jsx(FormattedMessage, {
      message: `pricing-pages.bizObjectives.${bizObjKey}`
    });

    const isNew = bizObjKey === STREAMLINE_B2B_BUYING || bizObjKey === GROW_YOUR_REVENUE;

    const newBadge = /*#__PURE__*/_jsx(UIBadge, {
      use: "new",
      className: "m-left-3",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "pricingPage.new"
      })
    });

    if (bizObj.breakoutData) {
      return /*#__PURE__*/_jsx(BizObjHeaderLink, {
        use: "link",
        onClick: partial(onBizObjHeaderClick, bizObjKey),
        children: textNode
      });
    }

    return /*#__PURE__*/_jsxs(BizObjHeaderNoLink, {
      onClick: partial(onBizObjHeaderClick, bizObjKey),
      children: [textNode, isNew && newBadge]
    });
  };

  const renderFeatures = bizObjKey => {
    const bizObjectiveFeatures = BizObjectivesMap[bizObjKey].features;
    const filteredFeatures = getLangEligibleFeatureIds(bizObjectiveFeatures);
    return filteredFeatures.map(feature => {
      const secondaryMessage = feature === PAYMENTS ? 'pricingPage.highlightedMerchCards.usOnly' : null;
      return /*#__PURE__*/_jsx(UIFlex, {
        className: "m-y-1",
        children: /*#__PURE__*/_jsx(ChecklistItem, {
          tooltipMessage: getProductFeatureDescriptionKey(feature, productLine),
          message: `pricing-pages.features.${feature}.name`,
          secondaryMessage: secondaryMessage
        })
      }, feature);
    });
  };

  const renderBizObjective = bizObjKey => {
    return /*#__PURE__*/_jsxs("div", {
      className: "m-bottom-9",
      children: [renderBizObjectiveHeader(bizObjKey), renderFeatures(bizObjKey)]
    }, bizObjKey);
  };

  const {
    lowerTierPlus
  } = HighlightedProductsHeaderConfig[productLine][productLevel];
  const isMon569Variant = useSelector(isMon569VariantSelector);
  if (isExperimentTreatmentRequestLoading) return null;
  const LowerTierMessage = productLevel === FREE ? /*#__PURE__*/_jsx(FormattedMessage, {
    message: "pricingPage.freeProductCards.bizObjectivesTitle"
  }) : productLevel === STARTER && productLine !== CMS ? /*#__PURE__*/_jsx(FormattedReactMessage, {
    message: "pricingPage.highlightedMerchCards.freeToolsPlus",
    options: {
      freeToolsWithIncreasedLimits: /*#__PURE__*/_jsx(UILink, {
        onClick: toggleViewAllFeaturesModal,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "pricingPage.highlightedMerchCards.freeToolsWithLimits"
        })
      })
    }
  }) : /*#__PURE__*/_jsx(FormattedMessage, {
    message: lowerTierPlus
  });

  const renderControlBody = () => {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Section, {
        "data-selenium-id": "biz-objectives-1",
        isMon569Variant: isMon569Variant,
        className: "p-top-8",
        style: Object.assign({
          msGridRow: 6,
          gridRowStart: 6
        }, commonStyles),
        children: /*#__PURE__*/_jsx(BizLowerTierContainer, {
          children: LowerTierMessage
        })
      }), /*#__PURE__*/_jsx(Section, {
        "data-selenium-id": "biz-objectives-2",
        isMon569Variant: isMon569Variant,
        style: Object.assign({
          msGridRow: 7,
          gridRowStart: 7
        }, commonStyles),
        children: HighlightedProductsBizObjectives[productLine][productLevel].map(bizKey => renderBizObjective(bizKey))
      })]
    });
  };

  const Mon607bizObject = HighlightedProductsBizObjectives[`${productLine}_MON607`][productLevel];

  const renderVariantBody1 = () => {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Section, {
        "data-selenium-id": "biz-objectives-1",
        isMon569Variant: isMon569Variant,
        className: "p-top-8",
        style: Object.assign({
          msGridRow: 6,
          gridRowStart: 6
        }, commonStyles),
        children: /*#__PURE__*/_jsx(BizLowerTierContainer, {
          children: LowerTierMessage
        })
      }), /*#__PURE__*/_jsxs(Section, {
        "data-selenium-id": "biz-objectives-2",
        isMon569Variant: isMon569Variant,
        style: Object.assign({
          msGridRow: 7,
          gridRowStart: 7
        }, commonStyles),
        children: [Mon607bizObject[MON607_BIZ_OBJECTIVE_CATEGORIES.branding] && Mon607bizObject[MON607_BIZ_OBJECTIVE_CATEGORIES.branding].map(bizKey => renderBizObjective(bizKey)), Mon607bizObject.regular.map(bizKey => renderBizObjective(bizKey))]
      })]
    });
  };

  const renderVariantBody2 = () => {
    if (Object.keys(Mon607bizObject).includes(MON607_BIZ_OBJECTIVE_CATEGORIES.branding)) {
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(Section, {
          "data-selenium-id": "biz-objectives-1",
          isMon569Variant: isMon569Variant,
          className: "p-top-8",
          style: Object.assign({
            msGridRow: 6,
            gridRowStart: 6
          }, commonStyles),
          children: /*#__PURE__*/_jsx(BizLowerTierContainer, {
            children: /*#__PURE__*/_jsx(FormattedReactMessage, {
              message: "pricingPage.highlightedMerchCards.freeToolsRemoveBranding",
              options: {
                freeToolsWithIncreasedLimits: /*#__PURE__*/_jsx(UILink, {
                  onClick: toggleViewAllFeaturesModal,
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "pricingPage.highlightedMerchCards.freeToolsWithLimits"
                  })
                })
              }
            })
          })
        }), /*#__PURE__*/_jsxs(Section, {
          "data-selenium-id": "biz-objectives-2",
          isMon569Variant: isMon569Variant,
          className: "p-bottom-4",
          style: Object.assign({
            msGridRow: 7,
            gridRowStart: 7
          }, commonStyles),
          children: [Mon607bizObject[MON607_BIZ_OBJECTIVE_CATEGORIES.branding].map(bizKey => renderFeatures(bizKey)), /*#__PURE__*/_jsx(BizLowerTierContainer, {
            className: "p-top-3",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "pricingPage.highlightedMerchCards.and"
            })
          }), Mon607bizObject[MON607_BIZ_OBJECTIVE_CATEGORIES.regular].map(bizKey => renderFeatures(bizKey))]
        })]
      });
    }

    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Section, {
        "data-selenium-id": "biz-objectives-1",
        isMon569Variant: isMon569Variant,
        className: "p-top-8",
        style: Object.assign({
          msGridRow: 6,
          gridRowStart: 6
        }, commonStyles),
        children: LowerTierMessage
      }), /*#__PURE__*/_jsx(Section, {
        "data-selenium-id": "biz-objectives-2",
        isMon569Variant: isMon569Variant,
        style: Object.assign({
          msGridRow: 7,
          gridRowStart: 7
        }, commonStyles),
        children: Mon607bizObject[MON607_BIZ_OBJECTIVE_CATEGORIES.regular].map(bizKey => renderFeatures(bizKey))
      })]
    });
  };

  const renderBizObjectiveBody = () => {
    if (mon607Variant === MON607_PARAMETER_OPTIONS.VARIANT1) {
      return renderVariantBody1();
    } else if (mon607Variant === MON607_PARAMETER_OPTIONS.VARIANT2) {
      return renderVariantBody2();
    }

    return renderControlBody();
  };

  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [renderBizObjectiveBody(), isModalOpen && /*#__PURE__*/_jsx(FeatureModalSplitter, {
      onCloseClick: toggleViewAllFeaturesModal,
      productLine: productLine,
      defaultProductLevel: productLevel
    })]
  });
};

BizObjectives.propTypes = {
  productLine: PropTypes.string.isRequired,
  productLevel: PropTypes.string.isRequired,
  commonStyles: PropTypes.object.isRequired
};
export default BizObjectives;